import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Container,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  ViewWeek as WeekIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { moldHistoryService } from '../services/mold-history.service';
import { MoldHistory, HistoryType, HistoryFormData } from '../types/history.types';
import { HistoryListTable, HistoryFilter, HistoryFormModal, HistoryDetailModal, CalendarView } from '../components/history';
import { Customer, Mold } from '../types';
import { customerService } from '../services/customer.service';
import { getMolds, getMoldsByCustomerId } from '../services/mold.service';
import { useAuth } from '../context/AuthContext';

type ViewMode = 'list' | 'calendar' | 'week';

const MoldHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [histories, setHistories] = useState<MoldHistory[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHistory, setEditingHistory] = useState<MoldHistory | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailHistory, setDetailHistory] = useState<MoldHistory | null>(null);
  
  // 필터 상태
  const [selectedType, setSelectedType] = useState<HistoryType | 'all'>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchText, setSearchText] = useState('');
  
  // 추가: 고객사/금형 관련 상태
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [molds, setMolds] = useState<Mold[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | 'all'>('all');
  const [selectedMoldId, setSelectedMoldId] = useState<string | 'all'>('all');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // 고객사/협력사 여부 확인
      const isCustomerUser = user?.role === 'customer' || user?.role === 'partner';
      
      // 금형 데이터 조회 (권한별)
      let moldsData: Mold[];
      if (isCustomerUser && user?.customerId) {
        // 고객사/협력사: 자사 금형만
        moldsData = await getMoldsByCustomerId(user.customerId);
      } else {
        // admin/user: 전체 금형
        moldsData = await getMolds();
      }
      
      // 이력 데이터 조회
      const allHistories = await moldHistoryService.getAll();
      
      // 고객사/협력사는 자사 금형의 이력만 필터링
      let historiesData: MoldHistory[];
      if (isCustomerUser && moldsData.length > 0) {
        const moldIds = moldsData.map(m => m.id).filter((id): id is string => id !== undefined);
        historiesData = allHistories.filter(h => moldIds.includes(h.moldId));
      } else {
        historiesData = allHistories;
      }
      
      // 고객사 데이터 (admin/user만 필요)
      const customersData = isCustomerUser ? [] : await customerService.getAll();
      
      setHistories(historiesData);
      setCustomers(customersData);
      setMolds(moldsData);
    } catch (error) {
      console.error('데이터 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (_: React.SyntheticEvent, newValue: ViewMode) => {
    setViewMode(newValue);
  };

  const handleAddHistory = () => {
    setEditingHistory(null);
    setModalOpen(true);
  };

  const handleViewHistory = (history: MoldHistory) => {
    setDetailHistory(history);
    setDetailModalOpen(true);
  };

  const handleEditHistory = (history: MoldHistory) => {
    setEditingHistory(history);
    setModalOpen(true);
  };

  const handleEditFromDetail = () => {
    if (detailHistory) {
      setDetailModalOpen(false);
      setEditingHistory(detailHistory);
      setModalOpen(true);
    }
  };

  const handleDeleteFromDetail = () => {
    if (detailHistory) {
      setDetailModalOpen(false);
      handleDeleteHistory(detailHistory);
    }
  };

  const handleDeleteHistory = async (history: MoldHistory) => {
    if (!window.confirm(`"${history.title}" 이력을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await moldHistoryService.delete(history.id!);
      await loadInitialData();
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleSubmitHistory = async (data: HistoryFormData & { existingFiles?: any[] }) => {
    try {
      // files, existingFiles 분리
      const { files, existingFiles, ...historyData } = data;
      
      if (editingHistory) {
        // 수정 - existingFiles도 함께 전달
        await moldHistoryService.update(editingHistory.id!, historyData, files, existingFiles);
      } else {
        // 신규 등록
        await moldHistoryService.create(historyData, files);
      }
      
      await loadInitialData(); // 목록 갱신
      setModalOpen(false);
      setEditingHistory(null);
    } catch (error) {
      console.error('이력 처리 실패:', error);
      throw error;
    }
  };

  // 필터링된 이력
  const filteredHistories = useMemo(() => {
    return histories
      .filter((history) => {
        // 금형 필터
        if (selectedMoldId !== 'all' && history.moldId !== selectedMoldId) {
          return false;
        }

        // 타입 필터
        if (selectedType !== 'all' && history.type !== selectedType) {
          return false;
        }

        // 날짜 필터
        if (startDate && history.date < startDate) {
          return false;
        }
        if (endDate && history.date > endDate) {
          return false;
        }

        // 검색 필터
        if (searchText) {
          const search = searchText.toLowerCase();
          return (
            history.moldNumber.toLowerCase().includes(search) ||
            history.title.toLowerCase().includes(search) ||
            history.description.toLowerCase().includes(search)
          );
        }

        return true;
      })
      .sort((a, b) => {
        // createdAt 기준 내림차순 정렬 (최신순)
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return bTime - aTime;
      });
  }, [histories, selectedMoldId, selectedType, startDate, endDate, searchText]);

  // 달력용 이벤트 데이터 변환
  const calendarEvents = useMemo(() => {
    return filteredHistories.map((history) => ({
      title: `[${history.moldNumber}] ${history.title}`,
      start: history.date,
      end: history.date,
      resource: {
        id: history.id!,
        type: history.type,
        moldName: history.moldNumber,
      },
    }));
  }, [filteredHistories]);

  const handleResetFilter = () => {
    setSelectedCustomerId('all');
    setSelectedMoldId('all');
    setSelectedType('all');
    setStartDate(null);
    setEndDate(null);
    setSearchText('');
  };

  const handleCustomerChange = (customerId: string | 'all') => {
    setSelectedCustomerId(customerId);
    // 고객사 변경 시 금형 선택 초기화
    setSelectedMoldId('all');
  };

  const renderListView = () => (
    <Box sx={{ mt: 2 }}>
      <HistoryFilter
        selectedType={selectedType}
        startDate={startDate}
        endDate={endDate}
        searchText={searchText}
        onTypeChange={setSelectedType}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onSearchChange={setSearchText}
        onReset={handleResetFilter}
        customers={customers}
        molds={molds}
        selectedCustomerId={selectedCustomerId}
        selectedMoldId={selectedMoldId}
        onCustomerChange={handleCustomerChange}
        onMoldChange={setSelectedMoldId}
      />
      <HistoryListTable
        histories={filteredHistories}
        molds={molds}
        onView={handleViewHistory}
        onEdit={handleEditHistory}
        onDelete={handleDeleteHistory}
      />
    </Box>
  );

  const renderCalendarView = () => (
    <Box sx={{ mt: 2 }}>
      <HistoryFilter
        selectedType={selectedType}
        startDate={startDate}
        endDate={endDate}
        searchText={searchText}
        onTypeChange={setSelectedType}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onSearchChange={setSearchText}
        onReset={handleResetFilter}
        customers={customers}
        molds={molds}
        selectedCustomerId={selectedCustomerId}
        selectedMoldId={selectedMoldId}
        onCustomerChange={handleCustomerChange}
        onMoldChange={setSelectedMoldId}
      />
      <CalendarView
        events={calendarEvents}
        onEventClick={(event) => {
          const history = filteredHistories.find((h) => h.id === event.resource?.id);
          if (history) {
            handleViewHistory(history);
          }
        }}
      />
    </Box>
  );

  const renderWeekView = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body1" color="text.secondary">
        주간뷰 (구현 예정)
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          금형 이력관리
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddHistory}
          sx={{ px: 3 }}
        >
          이력 추가
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Tabs
            value={viewMode}
            onChange={handleViewChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab
              value="list"
              label="리스트"
              icon={<ListIcon />}
              iconPosition="start"
            />
            <Tab
              value="calendar"
              label="달력"
              icon={<CalendarIcon />}
              iconPosition="start"
            />
            <Tab
              value="week"
              label="주간"
              icon={<WeekIcon />}
              iconPosition="start"
              sx={{ display: 'none' }}
            />
          </Tabs>

          {loading ? (
            <Typography>로딩 중...</Typography>
          ) : (
            <>
              {viewMode === 'list' && renderListView()}
              {viewMode === 'calendar' && renderCalendarView()}
              {viewMode === 'week' && renderWeekView()}
            </>
          )}
        </CardContent>
      </Card>

      {/* 이력 추가/수정 모달 */}
      <HistoryFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingHistory(null);
        }}
        onSubmit={handleSubmitHistory}
        molds={molds}
        initialData={editingHistory ? {
          moldId: editingHistory.moldId,
          type: editingHistory.type,
          title: editingHistory.title,
          description: editingHistory.description,
          date: editingHistory.date,
          shots: editingHistory.shots,
          purpose: editingHistory.purpose,
          expectedReturn: editingHistory.expectedReturn,
          productionQty: editingHistory.productionQty,
          defectQty: editingHistory.defectQty,
          cost: editingHistory.cost,
          vendor: editingHistory.vendor,
          inspector: editingHistory.inspector,
          inspectionResult: editingHistory.inspectionResult,
          disposalReason: editingHistory.disposalReason,
          disposalMethod: editingHistory.disposalMethod,
          files: editingHistory.files as any, // HistoryFile[] → any로 전달
        } : undefined}
      />

      {/* 이력 상세보기 모달 */}
      <HistoryDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailHistory(null);
        }}
        history={detailHistory}
        molds={molds}
        onEdit={handleEditFromDetail}
        onDelete={handleDeleteFromDetail}
      />
    </Container>
  );
};

export default MoldHistoryPage;
